import { useEffect, useState } from 'react';
import type { Key } from 'react-aria-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { Button } from '../tailgrids/core/button.js';
import { CollapsibleGroup } from '../tailgrids/core/collapsible.js';
import { cn } from '../../utils/cn.js';
import { useAuthStore, useUIStore } from '../../store/index.js';
import { evaluateClientPolicy } from '../../auth/policies.js';
import { useAllEnabledNavItems } from '../../modules/useNavItems.js';
import { useUiMode } from '../../hooks/useUiMode.js';
import { buildNavTree, resolveActiveNav } from './nav-tree.js';
import { ShellNavGroup, ShellNavLink } from './ShellNavItem.js';
import { BrandMark } from './BrandMark.js';
import { CloseIcon, SidebarExpandedIcon, ThreeDots } from './shell-icons.js';

interface ShellSidebarProps {
  /** Full width with labels, or the icon rail. */
  expanded: boolean;
  onToggle: () => void;
  /** Rendered inside the mobile sheet: the toggle closes the sheet. */
  isMobileSheet?: boolean;
  /** Called after a link is followed (the mobile sheet closes itself). */
  onNavigate?: () => void;
}

/**
 * Template example: NextAdmin `components/common/sidebar/index.tsx` (commit e0b72a9aa69e): header with brand and toggle,
 * sectioned navigation, footer card. The footer card holds the simple-view switch instead of the upgrade offer.
 */
export function ShellSidebar({ expanded, onToggle, isMobileSheet = false, onNavigate }: ShellSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUiMode } = useUiMode();
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);
  const organizationPlan = useAuthStore((s) => s.user?.organizationPlan ?? null);
  const navItems = useAllEnabledNavItems();

  const tree = buildNavTree(navItems, (policy) => evaluateClientPolicy(policy, permissions, organizationPlan));
  const active = resolveActiveNav(tree, location.pathname);

  // One group open at a time; following a link into another group opens that group.
  const [expandedKeys, setExpandedKeys] = useState<Set<Key>>(() => new Set(active.group ? [active.group] : []));
  useEffect(() => {
    if (active.group)
      setExpandedKeys((keys) => (keys.has(active.group as string) ? keys : new Set([active.group as string])));
  }, [active.group]);

  const introSeen = useUIStore((s) => s.simpleViewIntroSeen);
  const markIntroSeen = useUIStore((s) => s.markSimpleViewIntroSeen);

  const openSimpleView = () => {
    markIntroSeen();
    setUiMode('simple');
    // A conversation open in the detailed shell reopens on the landing as its selection.
    const match = /^\/conversations\/([^/?#]+)/.exec(location.pathname);
    navigate(match ? `/?c=${match[1]}` : '/');
    onNavigate?.();
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div
        className={cn(
          'flex items-center px-4 pt-7 text-text-primary',
          expanded ? 'justify-between' : 'flex-col justify-center gap-4',
        )}
      >
        <Link to="/" onClick={onNavigate} aria-label="Conflui home">
          <BrandMark withText={expanded} />
        </Link>
        <button
          type="button"
          onClick={onToggle}
          className={cn(
            'rounded-lg p-1.5 text-icon-tertiary transition-colors',
            isMobileSheet ? 'hover:bg-background-gray-primary hover:text-text-primary' : 'hover:text-text-secondary',
          )}
          aria-label={isMobileSheet ? 'Close navigation' : expanded ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {isMobileSheet ? <CloseIcon /> : <SidebarExpandedIcon />}
        </button>
      </div>

      <nav
        aria-label="Main navigation"
        className={cn('scrollbar-thin flex-1 overflow-y-auto', expanded ? 'mt-7 space-y-6 px-4' : 'mt-5 px-2')}
      >
        <div className={cn('space-y-1', !expanded && 'space-y-1.5')}>
          {tree.topItems.map((item) => (
            <ShellNavLink
              key={item.path}
              item={item}
              collapsed={!expanded}
              activePath={active.path}
              onNavigate={onNavigate}
            />
          ))}
        </div>

        <div>
          {/* Expanded: section label | collapsed: three-dot divider between sections */}
          {tree.groups.length > 0 &&
            (expanded ? (
              <p className="mt-6 mb-4 text-xs text-text-tertiary uppercase">Menu</p>
            ) : (
              <span className="flex items-center justify-center pt-6 pb-4 text-icon-secondary">
                <ThreeDots />
              </span>
            ))}
          <CollapsibleGroup
            expandedKeys={expandedKeys}
            onExpandedChange={setExpandedKeys}
            className={cn(expanded ? 'space-y-1' : 'space-y-1.5')}
          >
            {tree.groups.map((group) => (
              <ShellNavGroup
                key={group.label}
                group={group}
                collapsed={!expanded}
                activePath={active.path}
                onNavigate={onNavigate}
              />
            ))}
          </CollapsibleGroup>
        </div>
      </nav>

      {/* Footer: the introduction card until the person has opened the simple view once, then a plain row; the
          rail keeps an icon button for the same action */}
      {expanded && introSeen ? (
        <div className="border-t border-card-border px-4 py-3">
          <button
            type="button"
            onClick={openSimpleView}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-text-secondary transition-colors duration-200 hover:bg-sidebar-navigation-nav-item-nav-hover-background hover:text-text-primary"
          >
            <MessageSquare size={18} className="text-icon-tertiary" aria-hidden="true" />
            <span>Simple view</span>
          </button>
        </div>
      ) : expanded ? (
        <div className="px-4 py-4">
          <div className="rounded-2xl bg-background-gray-primary px-4 py-5 text-center">
            <p className="mb-2 leading-6 font-semibold text-text-primary">Simple view</p>
            <small className="text-sm leading-5 tracking-[-0.15px] text-text-tertiary">
              Talk to Conflui in one conversation instead of the full app
            </small>
            <Button size="lg" className="mt-4 h-10 w-full bg-brand-500" onPress={openSimpleView}>
              Open simple view
            </Button>
          </div>
        </div>
      ) : (
        <div className="px-2 py-4">
          <button
            type="button"
            onClick={openSimpleView}
            className="flex w-full items-center justify-center rounded-lg px-3 py-2.5 text-icon-tertiary transition-colors hover:bg-sidebar-navigation-nav-item-nav-hover-background hover:text-icon-primary"
            aria-label="Switch to the simple view"
          >
            <MessageSquare size={20} />
          </button>
        </div>
      )}
    </div>
  );
}
